import { Modal } from 'react-bootstrap'
import { useMemo } from 'react'

const PRIVILEGE_KEYS = ['view', 'edit', 'delete', 'set_manager']

function formatPrivilegeLabel(key) {
    return key.replace(/_/g, ' ')
}

function grantedPrivileges(privileges) {
    if (!privileges) {
        return []
    }
    return PRIVILEGE_KEYS.filter((key) => privileges[key] !== false && privileges[key] != null)
}

function buildPortalRows(portal, roleId) {
    if (!Array.isArray(portal)) {
        return []
    }
    return portal
        .map((resource) => {
            const roleEntry = resource.roles?.find((r) => r.id === roleId)
            if (!roleEntry) {
                return null
            }
            const granted = grantedPrivileges(roleEntry.privileges)
            return {
                id: resource.id,
                resource: resource.resource,
                notes: resource.notes,
                granted,
                privilegeDetails: roleEntry.privileges,
            }
        })
        .filter(Boolean)
}

function buildApiRows(api, roleId) {
    if (!Array.isArray(api)) {
        return []
    }
    return api
        .map((resource) => {
            const roleEntry = resource.roles?.find((r) => r.id === roleId)
            if (!roleEntry) {
                return null
            }
            const granted = grantedPrivileges(roleEntry.privileges)
            return {
                id: resource.id,
                resource: resource.resource,
                uri: resource.uri,
                notes: resource.notes,
                granted,
                privilegeDetails: roleEntry.privileges,
            }
        })
        .filter(Boolean)
}

const RoleModal = ({ show, onHide, role, authorizationData }) => {
    const portalRows = useMemo(
        () => (role?.id ? buildPortalRows(authorizationData?.portal, role.id) : []),
        [authorizationData?.portal, role?.id]
    )

    const apiRows = useMemo(
        () => (role?.id ? buildApiRows(authorizationData?.api, role.id) : []),
        [authorizationData?.api, role?.id]
    )

    if (!role) {
        return null
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Role Permissions: {role.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                {!authorizationData?.portal && !authorizationData?.api ? (
                    <p className="text-muted mb-0">Authorization data is not loaded yet.</p>
                ) : (
                    <>
                        <h6 className="text-uppercase text-muted small mb-2">Portal permissions</h6>
                        <p className="small text-muted mb-2">
                            Pages and features where this role is assigned ({portalRows.length} total).
                        </p>
                        <div className="table-responsive mb-4" style={{ maxHeight: '280px' }}>
                            <table className="table table-sm table-striped mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Resource</th>
                                        <th>Privileges</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {portalRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-muted">
                                                This role is not assigned to any portal permission.
                                            </td>
                                        </tr>
                                    ) : (
                                        portalRows.map((row) => (
                                            <tr key={row.id}>
                                                <td className="text-monospace small">{row.resource}</td>
                                                <td width="15%">
                                                    {row.granted.length === 0 ? (
                                                        <span className="text-muted">None</span>
                                                    ) : (
                                                        <ul className="mb-0 pl-3 small">
                                                            {row.granted.map((key) => (
                                                                <li key={key}>
                                                                    {formatPrivilegeLabel(key)}
                                                                 
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                                <td className="small text-muted">{row.notes || '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <h6 className="text-uppercase text-muted small mb-2">API permissions</h6>
                        <p className="small text-muted mb-2">
                            API resources where this role is assigned ({apiRows.length} total).
                        </p>
                        <div className="table-responsive" style={{ maxHeight: '200px' }}>
                            <table className="table table-sm table-striped mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Resource</th>
                                        <th>URI</th>
                                        <th>Privileges</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {apiRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-muted">
                                                This role is not assigned to any API resource.
                                            </td>
                                        </tr>
                                    ) : (
                                        apiRows.map((row) => (
                                            <tr key={row.id}>
                                                <td className="text-monospace small">{row.resource}</td>
                                                <td className="small text-monospace">{row.uri || '—'}</td>
                                                <td width="15%">
                                                    {row.granted.length === 0 ? (
                                                        <span className="text-muted">None</span>
                                                    ) : (
                                                        <ul className="mb-0 pl-3 small">
                                                            {row.granted.map((key) => (
                                                                <li key={key}>
                                                                    {formatPrivilegeLabel(key)}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    )
}

export default RoleModal
